/**
 * TRAD-1155 — 쿠키/추적 동의 상태.
 *
 * ── 이 파일이 하는 일 ────────────────────────────────────────────────────────
 * **배너를 띄우는 게 목적이 아니라 추적 초기화를 늦추는 게 목적이다.** 배너만 올리고 뒤에서
 * GA 가 이미 돌면 아무것도 안 고친 것이다. 그래서 이 모듈은 "동의했는가"라는 **순수한 상태**만
 * 다루고, 실제 초기화는 이 값을 읽는 쪽(`shared/configs/firebase.ts`,
 * `components/ad/AdBanner.tsx`)이 한다.
 *
 * 2026-09-10 운영 실측(비로그인·fresh context)에서 동의 절차 없이 아래가 발화하고 있었다:
 *   www.googletagmanager.com · firebase.googleapis.com
 *   firebaseinstallations.googleapis.com · www.google-analytics.com
 *
 * ── 저장 위치를 localStorage 로 고른 이유 ─────────────────────────────────────
 * 동의 여부 자체를 쿠키에 담으면 **동의를 받기 위해 쿠키를 쓰는 순환**이 된다. localStorage 는
 * 서버로 전송되지 않아 그 순환을 피한다.
 *
 * ── 🔴 왜 저장소를 주입받는 구조인가 (TRAD-1155 #869 실패에서 배움) ───────────
 * 처음엔 전역 `localStorage` 를 직접 쓰고, 테스트에서 그것을 monkey-patch 해
 * "던지는 환경"을 흉내 냈다. **로컬에선 통과했는데 CI 에서 세 개가 빨간불이었다** —
 * `Object.defineProperty(localStorage, 'getItem', …)` 가 CI 환경에서는 안 먹어서
 * **전제 자체가 안 걸렸다.** 환경마다 전역 저장소의 구현이 달라서 생긴 일이다.
 *
 * 그래서 **경계(전역 접근)와 로직(값 해석)을 갈랐다.** 아래 `readConsentFrom`·`persistConsent`
 * 는 저장소를 인자로 받는 순수에 가까운 함수라 테스트가 **던지는 객체를 그냥 넘기면 된다** —
 * 전역을 건드리지 않으므로 환경에 안 기댄다. 공개 API(`readConsent`/`writeConsent`/
 * `isTrackingAllowed`/`shouldAskForConsent`)는 그대로다.
 */

import { currentConsentRegion, type ConsentRegion } from '@/shared/lib/consent-region';

/** 동의 선택을 담는 localStorage 키. */
export const COOKIE_CONSENT_STORAGE_KEY = 'cookie-consent';

/**
 * 사용자의 선택.
 * - `granted` — 추적 초기화 허용
 * - `denied`  — 거부. **거부도 저장한다** (안 그러면 재방문마다 다시 묻는다)
 * - `null`    — 아직 안 물어봄 → 배너를 띄우고 **추적은 시작하지 않는다**
 */
export type ConsentChoice = 'granted' | 'denied';

/** 이 창에서 선택이 바뀌었을 때 발화하는 이벤트 이름. */
export const CONSENT_CHANGED_EVENT = 'cookie-consent:changed';

/** 이 모듈이 저장소에게 요구하는 최소 규격. 테스트는 이걸 만족하는 아무 객체나 주입하면 된다. */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * 전역 저장소를 얻는다 — **이 파일에서 유일하게 환경에 기대는 자리**.
 *
 * 규격 확인(`typeof getItem === 'function'`)까지 하는 이유는 일부 런타임이 전역
 * `localStorage` 를 빈 객체로 흉내내기 때문이다(`src/i18n/index.ts` 와 같은 방어이고,
 * 의도적으로 같은 모양으로 맞췄다 — 두 곳이 갈리면 한쪽만 고쳐진다).
 */
export const resolveStorage = (): StorageLike | null => {
  try {
    if (typeof localStorage === 'undefined' || localStorage === null) return null;
    if (typeof localStorage.getItem !== 'function' || typeof localStorage.setItem !== 'function') {
      return null;
    }
    return localStorage;
  } catch {
    // 접근 자체가 던지는 환경(사이트 데이터 차단 등)
    return null;
  }
};

/**
 * 주입된 저장소에서 선택을 읽는다. 없거나 읽을 수 없으면 `null`.
 *
 * 🔴 **읽기 실패는 `denied` 가 아니라 `null` 이다.** `denied` 로 폴백하면 배너가 안 뜨고
 * 사용자는 영원히 선택할 기회를 못 받는다. `null` 이면 배너가 떠서 다시 물어본다 —
 * 추적은 여전히 시작되지 않으므로 **안전한 쪽으로 틀린다.**
 */
export const readConsentFrom = (storage: StorageLike | null): ConsentChoice | null => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    return raw === 'granted' || raw === 'denied' ? raw : null;
  } catch (e) {
    // raw error 를 삼키지 않는다 — 삼키면 "왜 매번 다시 묻지?" 를 추적할 수 없다.
    console.error('[cookie-consent] failed to read stored consent — will ask again', e);
    return null;
  }
};

/**
 * 주입된 저장소에 선택을 기록한다. **던지지 않는다** — 성공 여부만 돌려준다.
 * 저장에 실패해도 호출부는 계속 진행해야 한다(이번 방문 동안은 선택이 존중되고,
 * 다음 방문에 다시 묻게 될 뿐이다).
 */
export const persistConsent = (storage: StorageLike | null, choice: ConsentChoice): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(COOKIE_CONSENT_STORAGE_KEY, choice);
    return true;
  } catch (e) {
    console.error('[cookie-consent] failed to persist consent — will ask again next visit', e);
    return false;
  }
};

/** 저장된 선택을 읽는다. 공개 API — 전역 저장소를 쓴다. */
export const readConsent = (): ConsentChoice | null => readConsentFrom(resolveStorage());

/**
 * 선택을 저장하고 같은 창의 구독자에게 알린다. 공개 API.
 *
 * 저장에 실패해도 이벤트는 발화한다 — **이번 방문 동안은 선택이 존중돼야** 하기 때문이다.
 */
export const writeConsent = (choice: ConsentChoice): void => {
  persistConsent(resolveStorage(), choice);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice }));
  }
};

/** 푸터 «쿠키 설정»이 배너를 다시 열 때 발화하는 이벤트 이름(TRAD-3792). */
export const CONSENT_PROMPT_EVENT = 'cookie-consent:open';

/**
 * 추적해도 되는가 — **순수 함수**(TRAD-3792). firebase.ts·AdBanner.tsx 가 같은 규칙을 쓰도록
 * 판정은 여기 한 곳뿐이다(두 곳이 갈리면 한쪽만 고쳐진다).
 *
 * | 저장된 선택 | opt-in 지역(EU·EEA·UK·CH·판정 실패) | opt-out 지역(한국 등) |
 * |---|---|---|
 * | `granted` | 추적 | 추적 |
 * | `denied`  | 안 함 | **안 함** — 거부는 지역과 무관하게 존중한다 |
 * | `null`    | 안 함(배너로 묻는다) | 추적(배너 없음, 거부는 푸터 «쿠키 설정») |
 *
 * 🔴 opt-in 에서 `=== 'granted'` 인 것이 TRAD-1155 의 핵심이다. `!== 'denied'` 로 쓰면 아직 안 물어본
 * 상태(`null`)가 통과해 배너가 떠 있는 동안 추적이 돈다.
 */
export const decideTracking = (choice: ConsentChoice | null, region: ConsentRegion): boolean => {
  if (choice === 'granted') return true;
  if (choice === 'denied') return false;
  return region === 'opt-out';
};

/** 배너를 띄워야 하는가 — 순수 함수. 아직 선택하지 않은 opt-in 지역 방문자만. */
export const decideAsk = (choice: ConsentChoice | null, region: ConsentRegion): boolean =>
  choice === null && region === 'opt-in';

/** 추적을 시작해도 되는가. **초기화하는 쪽이 물어보는 단 하나의 질문이다.** */
export const isTrackingAllowed = (): boolean => decideTracking(readConsent(), currentConsentRegion());

/** 첫 방문에 배너를 띄워야 하는가. */
export const shouldAskForConsent = (): boolean => decideAsk(readConsent(), currentConsentRegion());

/** 배너를 다시 연다(푸터 «쿠키 설정»). 지역·저장 상태와 무관하게 연다 — 언제든 바꿀 수 있어야 한다. */
export const openConsentPrompt = (): void => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(CONSENT_PROMPT_EVENT));
  }
};
